import { useEffect, useRef, useCallback } from 'react';

const SplashScreen = ({ onComplete }) => {
  const hasCalledComplete = useRef(false);
  
  // Stable callback reference to prevent re-renders issues
  const handleComplete = useCallback(() => {
    if (!hasCalledComplete.current) {
      hasCalledComplete.current = true;
      onComplete?.();
    }
  }, [onComplete]);

  useEffect(() => {
    // Single timer with stable callback
    const timer = setTimeout(handleComplete, 1500);
    return () => clearTimeout(timer);
  }, [handleComplete]);

  return (
    <div className="fixed inset-0 z-[100] bg-[#0d1829] flex flex-col items-center justify-center animate-fadeOut">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full animate-pulse"
          style={{
            background: 'radial-gradient(circle, rgba(0,212,255,0.3) 0%, rgba(0,212,255,0) 70%)',
          }}
        />
      </div>

      {/* Logo */}
      <div className="relative z-10 animate-scaleIn">
        <img 
          src="/icons/icon-192x192.png" 
          alt="XO Club" 
          className="w-20 h-20 rounded-2xl shadow-2xl"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </div>

      {/* App name */}
      <h1 className="mt-4 text-2xl font-black tracking-wider animate-slideUp">
        <span className="text-cyan-400">XO</span>
        <span className="text-white ml-2">CLUB</span>
      </h1>

      {/* Loading dots */}
      <div className="mt-6 flex gap-2">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-2 h-2 rounded-full bg-cyan-400"
            style={{ 
              animation: 'pulse 1s ease-in-out infinite',
              animationDelay: `${i * 200}ms` 
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes fadeOut {
          0%, 80% { opacity: 1; }
          100% { opacity: 0; pointer-events: none; }
        }
        @keyframes scaleIn {
          0% { transform: scale(0.8); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes slideUp {
          0% { transform: translateY(20px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        .animate-fadeOut {
          animation: fadeOut 1.5s ease-out forwards;
        }
        .animate-scaleIn {
          animation: scaleIn 0.5s ease-out forwards;
        }
        .animate-slideUp {
          animation: slideUp 0.5s ease-out 0.2s forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default SplashScreen;
